import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Container from '../common/Container';
import Card from '../common/Card';
import Button from '../common/Button';
import Text from '../common/Text';
import { assessmentService, MCQQuestion, Status, AssessmentType } from '../../api/services/assessment';
import { ArrowLeftIcon, ArrowRightIcon, FlagIcon } from '@heroicons/react/24/outline';
import FlagQuestionModal, { FlagReason } from './FlagQuestionModal';

interface Answer {
  questionId: string;
  selectedOptionId: string;
  timeSpent: number;
}

interface AssessmentAnswer {
  content_id: number;
  user_answer: string;
  time_spent: number;
}

const AssessmentQuestions: React.FC = () => {
  const { tenant, type, id } = useParams<{ tenant: string; type: string; id: string }>();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<MCQQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [isCompleting, setIsCompleting] = useState(false);
  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);
  const hasStartedAssessment = useRef(false);
  const newlyCreatedAssessment = useRef<any>(null);

  useEffect(() => {
    const initializeAssessment = async () => {
      try {
        let assessment;

        // If we're starting a new assessment
        if (type && !id && !hasStartedAssessment.current) {
          console.log('Starting new assessment of type:', type);
          hasStartedAssessment.current = true;
          setIsStarting(true);
          setLoading(true);
          try {
            assessment = await assessmentService.startAssessment(type as AssessmentType);
            if (assessment) {
              newlyCreatedAssessment.current = assessment;
              // Redirect to the assessment with the new ID
              navigate(`/${tenant}/skill-diagnostic/${type}/${assessment.id}`, { replace: true });
              return;
            }
          } catch (err) {
            console.error('Error starting assessment:', err);
            setError('Failed to start assessment');
            setIsStarting(false);
            setLoading(false);
            return;
          }
        }
        // If we have the newly created assessment and the ID matches, use it immediately
        if (newlyCreatedAssessment.current && id && newlyCreatedAssessment.current.id === parseInt(id)) {
          console.log('Using newly created assessment:', newlyCreatedAssessment.current);
          assessment = newlyCreatedAssessment.current;
          setIsStarting(false); // Clear starting state since we have the data
        }
        // If we have an assessment ID and it's not the newly created one, fetch it
        else if (id && (!newlyCreatedAssessment.current || newlyCreatedAssessment.current.id !== parseInt(id))) {
          console.log('Fetching existing assessment:', id);
          assessment = await assessmentService.getLatestAssessment();
        }

        if (assessment) {
          console.log('Fetched assessment:', assessment);
          setQuestions(assessment.questions);

          // If assessment is completed, navigate to summary
          if (assessment.status === Status.COMPLETED && assessment.results) {
            console.log('Assessment is completed, navigating to summary');
            const summaryState = {
              totalQuestions: assessment.results.total_questions,
              correctAnswers: assessment.results.correct_answers,
              totalTime: assessment.results.time_taken,
              timePerQuestion: assessment.results.time_per_question,
              answers: assessment.answers?.reduce((acc: Record<string, string>, answer: AssessmentAnswer) => ({
                ...acc,
                [answer.content_id]: answer.user_answer
              }), {}) || {},
              questions: assessment.questions,
              score: assessment.results.score,
              recommendations: assessment.results.recommendations
            };

            navigate(`/${tenant}/skill-diagnostic/${assessment.type}/${assessment.id}/summary`, {
              state: summaryState,
              replace: true
            });
            return;
          }

          // If assessment is in progress, set current question and answers
          if (assessment.answers) {
            const newAnswers = assessment.answers.map((answer: AssessmentAnswer) => ({
              questionId: answer.content_id.toString(),
              selectedOptionId: answer.user_answer,
              timeSpent: answer.time_spent
            }));
            setAnswers(newAnswers);
            setCurrentQuestionIndex(newAnswers.length);
            if (newAnswers.length < assessment.questions.length) {
              setSelectedOption(newAnswers[newAnswers.length]?.selectedOptionId || null);
            }
          }
        }
      } catch (err) {
        console.error('Error initializing assessment:', err);
        setError('Failed to load assessment');
      } finally {
        setLoading(false);
      }
    };

    initializeAssessment();
  }, [type, id, navigate]);

  const handleAnswerSelect = (option: string) => {
    setSelectedOption(option);
  };

  const handleNextQuestion = async () => {
    if (!id || !selectedOption) return;

    const timeSpent = Math.floor((Date.now() - startTime) / 1000); // Convert to seconds

    try {
      await assessmentService.submitAnswer(
        parseInt(id),
        questions[currentQuestionIndex].id.toString(),
        selectedOption,
        timeSpent,
        questions[currentQuestionIndex].type
      );

      // Update local answers state
      const newAnswers = [...answers];
      newAnswers[currentQuestionIndex] = {
        questionId: questions[currentQuestionIndex].id.toString(),
        selectedOptionId: selectedOption,
        timeSpent
      };
      setAnswers(newAnswers);

      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        // If the next question was already answered, load that answer
        setSelectedOption(newAnswers[currentQuestionIndex + 1]?.selectedOptionId || null);
        setStartTime(Date.now());
      } else {
        setIsCompleting(true);
        try {
          console.log('Completing assessment...');
          // Complete the assessment and get results
          const result = await assessmentService.completeAssessment(parseInt(id));
          console.log('Assessment completed with result:', result);

          // Safely handle the response data
          const timePerQuestion = result.timePerQuestion || [];
          const totalTime = timePerQuestion.reduce((a, b) => a + b, 0);

          const summaryState = {
            totalQuestions: result.totalQuestions || 0,
            correctAnswers: result.correctAnswers || 0,
            totalTime: totalTime,
            timePerQuestion: timePerQuestion,
            answers: newAnswers.reduce((acc, answer) => ({
              ...acc,
              [answer.questionId]: answer.selectedOptionId
            }), {}),
            questions: questions,
            score: result.score || 0,
            recommendations: result.recommendations || []
          };
          console.log('Navigating to summary with state:', summaryState);

          // Navigate to summary with results
          navigate(`/${tenant}/skill-diagnostic/${type}/${id}/summary`, {
            state: summaryState,
            replace: true
          });
        } catch (err) {
          console.error('Error completing assessment:', err);
          // TODO: Show error toast or message
        } finally {
          setIsCompleting(false);
        }
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      // TODO: Show error toast or message
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      // Load the previous answer if it exists
      setSelectedOption(answers[currentQuestionIndex - 1]?.selectedOptionId || null);
      setStartTime(Date.now());
    }
  };

  const handleFlagQuestion = async (reason: FlagReason, description?: string) => {
    if (!currentQuestion) return;

    try {
      await assessmentService.flagQuestion(
        currentQuestion.id,
        currentQuestion.type,
        reason,
        description
      );
      // Show success message (could use a toast library here)
      console.log('Question flagged successfully');
    } catch (error) {
      console.error('Failed to flag question:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  if (loading || isStarting) {
    return (
      <Container>
        <div className="flex flex-col items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
          {isStarting && (
            <Text variant="p" className="text-gray-600 dark:text-gray-400">
              Starting your assessment...
            </Text>
          )}
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Card className="max-w-2xl mx-auto">
          <div className="p-6 text-center">
            <Text variant="h2" className="text-red-600 mb-4">Error</Text>
            <Text variant="p" className="mb-6">{error}</Text>
            <Button
              onClick={() => navigate(`/${tenant}/skill-diagnostic`)}
              variant="secondary"
              size="md"
              leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
            >
              Back to Assessments
            </Button>
          </div>
        </Card>
      </Container>
    );
  }

  if (!questions.length) {
    return (
      <Container>
        <Card className="max-w-2xl mx-auto">
          <div className="p-6 text-center">
            <Text variant="h2" className="mb-4">No Questions Found</Text>
            <Text variant="p" className="mb-6">No questions were found for this assessment.</Text>
            <Button
              onClick={() => navigate(`/${tenant}/skill-diagnostic`)}
              variant="secondary"
              size="md"
              leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
            >
              Back to Assessments
            </Button>
          </div>
        </Card>
      </Container>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <Container>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </Container>
    );
  }

  // Safely access payload with fallbacks
  const payload = currentQuestion.payload;
  if (!payload || !payload.question || !payload.options) {
    return (
      <Container>
        <Card className="max-w-2xl mx-auto">
          <div className="p-6 text-center">
            <Text variant="h2" className="mb-4">Invalid Question</Text>
            <Text variant="p" className="mb-6">The question data is incomplete or invalid.</Text>
            <Button
              onClick={() => navigate(`/${tenant}/skill-diagnostic`)}
              variant="secondary"
              size="md"
              leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
            >
              Back to Assessments
            </Button>
          </div>
        </Card>
      </Container>
    );
  }

  const questionText = payload.question;
  const questionOptions = payload.options;

  return (
    <Container>
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Progress Indicator */}
        <div className="flex items-center justify-between">
          <Text variant="p" className="text-gray-600 dark:text-gray-300">
            Question {currentQuestionIndex + 1} of {questions.length}
          </Text>
          <div className="w-48 h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
            <div
              className="h-full bg-blue-600 rounded-full"
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <Card className="overflow-hidden">
          <div className="p-6 space-y-6">
            {/* Question Header with Flag Button */}
            <div className="flex items-start justify-between gap-4">
              <Text variant="h3" className="flex-1">{questionText}</Text>
              <button
                onClick={() => setIsFlagModalOpen(true)}
                className="flex-shrink-0 p-2 rounded-lg transition-colors text-yellow-500 dark:text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                title="Report this question"
              >
                <FlagIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {questionOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(option)}
                  className={`w-full p-4 text-left rounded-lg border transition-colors ${
                    selectedOption === option
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-500'
                  }`}
                >
                  <Text variant="p">{option}</Text>
                </button>
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={handlePreviousQuestion}
                disabled={currentQuestionIndex === 0}
                variant="secondary"
                size="md"
                className="w-32 sm:w-36"
                leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
              >
                Previous
              </Button>
              <Button
                onClick={handleNextQuestion}
                disabled={!selectedOption || isCompleting}
                variant="primary"
                size="md"
                className="w-32 sm:w-36"
                rightIcon={<ArrowRightIcon className="w-5 h-5" />}
              >
                {isCompleting ? 'Completing...' : currentQuestionIndex === questions.length - 1 ? 'Finish' : 'Next'}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Flag Question Modal */}
      <FlagQuestionModal
        isOpen={isFlagModalOpen}
        onClose={() => setIsFlagModalOpen(false)}
        onFlag={handleFlagQuestion}
        questionText={questionText}
      />
    </Container>
  );
};

export default AssessmentQuestions;